import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  MenuItem,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import CEDatePicker from "components/CEDatePicker";
import dayjs from "dayjs";
import Grid from "@mui/material/Grid2";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import { useStateContext } from "contexts/ContextProvider";
import { OVERLAY_NAMES } from "constants/Overlays";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";
import Overlay from "components/Overlay/Overlay";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";

const getTodayDateValue = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const MemberNewEdit = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();

  const { overlayData, setOverlayName, triggerPageRefresh } = useStateContext();
  const { showSnackbar } = useStateContext();

  usePageTitle(
    overlayData.Action === OVERLAY_NAMES.MEMBER_ACTION_NEW
      ? "New Member"
      : "Edit Member",
  );

  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [isEditable, setIsEditable] = useState(true);

  const isNewMember = overlayData.Action === OVERLAY_NAMES.MEMBER_ACTION_NEW;

  // Edit lock for existing members only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "member",
    isNewMember ? null : overlayData.Member?.id,
  );

  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [hourlyRateError, setHourlyRateError] = useState(false);

  const [name, setName] = useState(
    isNewMember ? "" : overlayData.Member.Name || "",
  );

  const [email, setEmail] = useState(
    isNewMember ? "" : overlayData.Member.Email || "",
  );

  const [role, setRole] = useState(
    isNewMember ? "" : overlayData.Member.Role || "",
  );

  const [hourlyRate, setHourlyRate] = useState(
    isNewMember
      ? "0.00"
      : String(Number.parseFloat(overlayData.Member?.HourlyRate || 0).toFixed(2)),
  );
  const [initialHourlyRate] = useState(
    isNewMember
      ? "0.00"
      : String(Number.parseFloat(overlayData.Member?.HourlyRate || 0).toFixed(2)),
  );
  const [hourlyRateEffectiveDate, setHourlyRateEffectiveDate] = useState(
    getTodayDateValue(),
  );

  const [generatedPassword, setGeneratedPassword] = useState("");

  function saveChanges() {
    // Validation
    if (!name.trim()) {
      setNameError(true);
      showSnackbar("Name is required", theme, "error");
      return;
    }

    if (!email.trim()) {
      setEmailError(true);
      showSnackbar("Email is required", theme, "error");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError(true);
      showSnackbar("Invalid email format", theme, "error");
      return;
    }

    const parsedHourlyRate = Number.parseFloat(hourlyRate);
    if (!Number.isFinite(parsedHourlyRate) || parsedHourlyRate < 0) {
      setHourlyRateError(true);
      showSnackbar("Hourly rate must be a valid non-negative number", theme, "error");
      return;
    }

    const normalizedHourlyRate = Number(parsedHourlyRate.toFixed(2));
    const hasHourlyRateChanged =
      !isNewMember &&
      Number.parseFloat(initialHourlyRate || "0") !== normalizedHourlyRate;

    if (hasHourlyRateChanged && !String(hourlyRateEffectiveDate || "").trim()) {
      showSnackbar("Please select an effective date for the hourly rate change", theme, "error");
      return;
    }

    setIsSavingChanges(true);
    setIsEditable(false);

    const data = {
      id: overlayData.MemberID,
      name: name,
      email: email,
      role: role,
      hourlyRate: normalizedHourlyRate,
    };

    if (hasHourlyRateChanged) {
      data.hourlyRateEffectiveDate = String(hourlyRateEffectiveDate || "").trim();
    }

    overlayData.Action === OVERLAY_NAMES.MEMBER_ACTION_NEW
      ? addNewMember(data)
      : updateMember(data);
  }

  function addNewMember(data) {
    api
      .post("/members", data)
      .then((response) => {
        showSnackbar("Member created successfully", theme, "success");
        setGeneratedPassword(response.data.temporaryPassword);

        // Emit socket event for real-time updates
        if (socket) {
          const newMemberId = response?.data?.member?.id ?? response?.data?.id;
          if (newMemberId) {
            socket.emit("member:saved", {
              modelType: "Member",
              recordId: newMemberId,
              changeAction: "Created",
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
            });
          }
        }

        triggerPageRefresh();
        // Don't close the overlay yet - show the password first
      })
      .catch((err) => {
        console.error(err);
        showSnackbar(
          err.response?.data?.error || "Error creating member",
          theme,
          "error",
        );
        setIsEditable(true);
      })
      .finally(() => {
        setIsSavingChanges(false);
      });
  }

  function updateMember(data) {
    api
      .put(`/members/${data.id}`, data)
      .then((response) => {
        const updatedTimesheetCount = Number(response?.data?.updatedTimesheetCount || 0);
        const didBackfill =
          data.hourlyRateEffectiveDate && Number.isFinite(updatedTimesheetCount);
        const message = didBackfill
          ? `Member updated successfully. ${updatedTimesheetCount} timeslot${updatedTimesheetCount === 1 ? "" : "s"} updated with the new rate.`
          : "Member updated successfully";
        showSnackbar(message, theme, "success");

        // Emit socket event for real-time updates
        if (socket) {
          socket.emit("member:saved", {
            modelType: "Member",
            recordId: data.id,
            changeAction: "Updated",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }

        triggerPageRefresh();
        setOverlayName("");
      })
      .catch((err) => {
        console.error(err);
        showSnackbar(
          err.response?.data?.error || "Error updating member",
          theme,
          "error",
        );
        setIsEditable(true);
      })
      .finally(() => {
        setIsSavingChanges(false);
      });
  }

  // Action buttons moved from the legacy header into the Overlay footer (R2).
  // Discard/Cancel keeps the outlined style; Save keeps the loading + disabled
  // (generated-password) guards. Mobile keeps the 40px icon variants.
  const footerActions = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        width: "100%",
        gap: 1,
      }}
    >
      {isMobile ? (
        <Tooltip title="Discard" placement="top">
          <IconButton
            color="primary"
            onClick={() => setOverlayName("")}
            sx={{
              width: "40px",
              height: "40px",
              border: `2px solid ${theme.palette.primary.main}`,
              borderRadius: "12px",
            }}
          >
            <DisabledByDefaultRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          color="primary"
          variant="outlined"
          size="large"
          endIcon={<DisabledByDefaultRoundedIcon />}
          onClick={() => setOverlayName("")}
          sx={{
            borderRadius: "12px",
          }}
        >
          Discard
        </Button>
      )}

      {isMobile ? (
        <Tooltip
          title={
            isSavingChanges
              ? "Saving..."
              : !!generatedPassword
                ? "Password shown"
                : "Save"
          }
          placement="top"
        >
          <span>
            <IconButton
              disabled={isSavingChanges || !!generatedPassword}
              color="primary"
              onClick={saveChanges}
              sx={{
                width: "40px",
                height: "40px",
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.background.default,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
                "&.Mui-disabled": {
                  backgroundColor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                },
              }}
            >
              <SaveRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Button
          loading={isSavingChanges}
          color="primary"
          variant="contained"
          size="large"
          endIcon={<SaveRoundedIcon />}
          onClick={saveChanges}
          disabled={isSavingChanges || !!generatedPassword}
          sx={{
            borderRadius: "12px",
          }}
        >
          Save
        </Button>
      )}
    </Box>
  );

  return (
    <>
    <Overlay
      id="member-new-edit"
      open={true}
      onClose={() => setOverlayName("")}
      title={
        overlayData.Action === OVERLAY_NAMES.MEMBER_ACTION_NEW
          ? "New Member"
          : "Edit Member"
      }
      height="fit-content"
      maxWidthPx={800}
      footer={footerActions}
    >
      <Box
        sx={{
          p: "1rem 2.3rem 1rem 2.5rem",
          width: "100%",
          "& .MuiTextField-root": {
            m: 0,
            width: "100%",
          },
        }}
      >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold">
                Member Information
              </Typography>
            </Grid>

            {generatedPassword && (
              <>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: theme.palette.success.light,
                      borderRadius: "8px",
                      border: `2px solid ${theme.palette.success.main}`,
                    }}
                  >
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      ✅ Member Created Successfully!
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      Temporary Password (share this securely):
                    </Typography>
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: theme.palette.background.default,
                        borderRadius: "4px",
                        fontFamily: "monospace",
                        fontSize: "1.1rem",
                        letterSpacing: "0.05em",
                        wordBreak: "break-all",
                        mt: 1,
                      }}
                    >
                      {generatedPassword}
                    </Box>
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ mt: 1, color: theme.palette.error.main }}
                    >
                      ⚠️ This password will only be shown once. Please copy it
                      before closing.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => {
                        navigator.clipboard.writeText(generatedPassword);
                        showSnackbar(
                          "Password copied to clipboard",
                          theme,
                          "success",
                        );
                      }}
                      sx={{ mt: 2 }}
                    >
                      Copy Password
                    </Button>
                  </Box>
                </Grid>
              </>
            )}

            <Grid size={{ xs: 12 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id="Name"
                label="Name"
                required
                error={nameError}
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setNameError(event.target.value.trim() === "");
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                disabled={
                  !isEditable ||
                  overlayData.Action === OVERLAY_NAMES.MEMBER_ACTION_EDIT
                }
                size="small"
                id="Email"
                label="Email"
                required
                error={emailError}
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setEmailError(event.target.value.trim() === "");
                }}
                helperText={
                  overlayData.Action === OVERLAY_NAMES.MEMBER_ACTION_EDIT
                    ? "Email cannot be changed"
                    : ""
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id="HourlyRate"
                label="Hourly Rate"
                type="number"
                required
                error={hourlyRateError}
                value={hourlyRate}
                onChange={(event) => {
                  setHourlyRate(event.target.value);
                  const parsedValue = Number.parseFloat(event.target.value);
                  setHourlyRateError(
                    event.target.value === "" ||
                      !Number.isFinite(parsedValue) ||
                      parsedValue < 0,
                  );
                }}
                slotProps={{
                  htmlInput: {
                    min: 0,
                    step: 0.01,
                  },
                }}
                helperText="Hourly compensation rate in USD"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>

            {!isNewMember &&
              Number.parseFloat(initialHourlyRate || "0") !==
                Number.parseFloat(String(hourlyRate || "0") || "0") && (
                <Grid size={{ xs: 12 }}>
                    <CEDatePicker
                    disabled={!isEditable}
                    size="small"
                    label="Rate Effective Date"
                      format="MM/DD/YYYY"
                      value={hourlyRateEffectiveDate ? dayjs(hourlyRateEffectiveDate) : null}
                      onChange={(newValue) => {
                        setHourlyRateEffectiveDate(
                          newValue?.isValid?.() ? newValue.format("YYYY-MM-DD") : "",
                        );
                      }}
                    slotProps={{
                        textField: {
                          size: "small",
                        },
                    }}
                    helperText="If this date is in the past, timeslots from that date until now will use the new rate."
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "&.MuiInputBase-root fieldset": {
                          py: 0,
                        },
                      },
                    }}
                  />
                </Grid>
              )}

            <Grid size={{ xs: 12 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                select
                id="Role"
                label="Role"
                value={role}
                onChange={(event) => {
                  setRole(event.target.value);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              >
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Member">Member</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Box>
    </Overlay>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </>
  );
};

export default MemberNewEdit;
